import { useState, useEffect, useCallback } from 'react';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ignoreProductionInformation, setChangeProductionInformation, setSetupProductionInformation, addProductionInformationV2, getNCFileById } from '@reducers/manufacture';
import { partsSearch } from '@reducers/search';
import { getPartsSearch } from '@reducers/parts';
import { parseTimeToSeconds, Alert } from '@helpers';
import {
  WizardStep,
  NCFileTypeOption,
  PartMode,
  PartData,
  TimeData,
  UseManageNCFileWizardNoERPParams,
  NCFileSearchItem,
  PartSearchItem,
  AddProductionInformationPayload,
  PaginatedResponse,
} from '../types';
import { INITIAL_PART_DATA, INITIAL_TIME_DATA, isSimpleType as isSimpleTypeFn } from '@components/common/NCFileType/NCFileWizardNoERP/constants';

const useManageNCFileWizardNoERP = ({ ncFileId, ncFileName, hide, productionBatchId }: UseManageNCFileWizardNoERPParams) => {
  const dispatch = useAppDispatch();

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [selectedType, setSelectedType] = useState<NCFileTypeOption>('linked');
  const [partMode, setPartMode] = useState<PartMode>('existing');
  const [partData, setPartData] = useState<PartData>({ ...INITIAL_PART_DATA });
  const [timeData, setTimeData] = useState<TimeData>({ ...INITIAL_TIME_DATA });
  const [skippedSteps, setSkippedSteps] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [ncFileData, setNcFileData] = useState<NCFileSearchItem | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PartSearchItem[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Load NC file data
  useEffect(() => {
    if (ncFileId) {
      (async () => {
        const data = await dispatch(getNCFileById(ncFileId));
        if (data) {
          setNcFileData(data);
        }
      })();
    }
  }, [ncFileId, dispatch]);

  const handleSearch = useCallback(
    async (query: string) => {
      setSearchLoading(true);
      const result = await dispatch(
        partsSearch({
          data: { search: query || '', top: 20, skip: 0 },
          resetHistory: true,
          ignoreHistory: true,
        }),
      );
      setSearchResults(result?.searchResult || []);
      setSearchLoading(false);
    },
    [dispatch],
  );

  const searchPartsForAutocomplete = useCallback(
    async (query: string): Promise<PaginatedResponse<PartSearchItem>> => {
      return await dispatch(getPartsSearch(query));
    },
    [dispatch],
  );

  // Load initial parts list when entering step 2
  useEffect(() => {
    if (currentStep === 2) {
      handleSearch('');
    }
  }, [currentStep]);

  const isSimpleType = isSimpleTypeFn(selectedType);

  const canSkip = !isSimpleType && (currentStep === 2 || currentStep === 3);

  const handleSave = useCallback(async () => {
    setLoading(true);

    if (isSimpleType) {
      const simpleThunkMap = {
        change: setChangeProductionInformation,
        ignore: ignoreProductionInformation,
        setup: setSetupProductionInformation,
      } as const;
      const thunk = simpleThunkMap[selectedType as keyof typeof simpleThunkMap];
      const response = (await dispatch(thunk({ ncFileId }))) as
        | { error?: boolean; errorDetails?: { errorMessage?: string } }
        | undefined;
      if (response?.error) {
        Alert({ title: response?.errorDetails?.errorMessage || 'Something went wrong', type: 'danger' });
      } else {
        hide();
      }
      setLoading(false);
      return;
    }

    const cycleTimeSeconds = parseTimeToSeconds(timeData.paidCycleTime);
    const setupTimeSeconds = parseTimeToSeconds(timeData.paidSetupTime);

    let activeTimeSeconds: number | undefined;
    let changeTimeSeconds: number | undefined;

    if (timeData.isManualSplit) {
      activeTimeSeconds = parseTimeToSeconds(timeData.activeTime);
      changeTimeSeconds = parseTimeToSeconds(timeData.changeTime);
    } else if (cycleTimeSeconds > 0) {
      activeTimeSeconds = Math.round(cycleTimeSeconds * 0.7);
      changeTimeSeconds = cycleTimeSeconds - activeTimeSeconds;
    }

    const partDetails = (partData.partId || partData.partNumber)
      ? {
          partName: partData.partNumber,
          description: partData.partDescription,
          drawing: partData.partDrawingNumber,
          revision: partData.partDrawingRevision,
          position: partData.partDrawingPosition,
        }
      : undefined;

    const payload: AddProductionInformationPayload = {
      ncFileId,
      ...(productionBatchId && { productionBatchId }),
      ...(partData.partId && { partId: partData.partId }),
      ...(partDetails && { partDetails }),
      clampingDetails: {
        runTimePerPcsSecond: activeTimeSeconds ?? null,
        changeTimePerPcsSecond: changeTimeSeconds ?? null,
        setupTimeSecond: setupTimeSeconds || null,
      },
    };

    const response = await dispatch(addProductionInformationV2(payload));

    if (response?.error) {
      Alert({ title: response?.errorDetails?.errorMessage || 'Something went wrong', type: 'danger' });
    } else {
      hide();
    }

    setLoading(false);
  }, [isSimpleType, selectedType, timeData, partData, ncFileId, productionBatchId, dispatch, hide]);

  const clearSkipped = useCallback(
    (step: number) => {
      setSkippedSteps((prev) => {
        if (!prev.has(step)) return prev;
        const next = new Set(prev);
        next.delete(step);
        return next;
      });
    },
    [],
  );

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      if (isSimpleType) {
        setCurrentStep(4);
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      clearSkipped(2);
      setCurrentStep(3);
      return;
    }

    if (currentStep === 3) {
      clearSkipped(3);
      setCurrentStep(4);
      return;
    }

    if (currentStep === 4) {
      await handleSave();
    }
  }, [currentStep, isSimpleType, handleSave, clearSkipped]);

  const handleSkip = useCallback(() => {
    setSkippedSteps((prev) => new Set([...prev, currentStep]));
    if (currentStep === 2) {
      setPartData({ ...INITIAL_PART_DATA });
      setCurrentStep(3);
    } else if (currentStep === 3) {
      setTimeData({ ...INITIAL_TIME_DATA });
      setCurrentStep(4);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep === 1) {
      hide();
      return;
    }
    if (currentStep === 4 && isSimpleType) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep((prev) => (prev - 1) as WizardStep);
  }, [currentStep, hide, isSimpleType]);

  const handleStepPress = useCallback(
    (step: WizardStep) => {
      if (step <= currentStep) {
        setCurrentStep(step);
      }
    },
    [currentStep],
  );

  return {
    currentStep,
    selectedType,
    setSelectedType,
    partMode,
    setPartMode,
    partData,
    setPartData,
    timeData,
    setTimeData,
    skippedSteps,
    loading,
    ncFileData,
    searchQuery,
    setSearchQuery,
    searchResults,
    searchLoading,
    handleSearch,
    searchPartsForAutocomplete,
    canSkip,
    handleNext,
    handleSkip,
    handleBack,
    handleStepPress,
  };
};

export default useManageNCFileWizardNoERP;
